import React, { useEffect, useMemo, useState } from "react";
import { Form, Input, Typography, Divider, Alert, message } from "antd";
import { useTranslation } from "react-i18next";
import type { Permission, RoleCreatePayload } from "../../../types/roleTypes";
import { getDirectorPermissions } from "services/roleService";
import CustomModal from "components/Common/CustomModal";
import PermissionSelector from "../components/PermissionSelector";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (roleData: any) => void;
  selectedRole: any; // if provided with .id => edit mode
};

const AddRoleModal: React.FC<Props> = ({ isOpen, onClose, onSave, selectedRole }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<RoleCreatePayload>();
  const [loading, setLoading] = useState(false);
  const [perms, setPerms] = useState<Permission[]>([]);
  const [saving, setSaving] = useState(false);

  const isEdit = !!selectedRole?.id;

  // Fetch the FULL permissions catalog whenever modal opens
  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    getDirectorPermissions()
      .then((res: any) => setPerms(res?.data || []))
      .catch(() => setPerms([]))
      .finally(() => setLoading(false));
  }, [isOpen]);

  // Build a Set of keys from selectedRole.permissions (for preselecting in edit mode)
  const selectedRoleKeys = useMemo(() => {
    if (!isEdit) return new Set<string>();
    const arr = Array.isArray(selectedRole?.permissions) ? selectedRole.permissions : [];
    return new Set<string>(arr.map((p: any) => `${p.action}::${p.subject}`));
  }, [isEdit, selectedRole]);

  // When modal opens or role changes, set form defaults
  useEffect(() => {
    if (!isOpen) return;

    if (!isEdit) {
      // Create mode
      form.setFieldsValue({
        name: "",
        description: "",
        permissionKeys: [],
      });
      return;
    }

    // Edit mode: prefill name/description and initial permission keys
    form.setFieldsValue({
      name: selectedRole?.name || "",
      description: selectedRole?.description ?? "",
      permissionKeys: Array.from(selectedRoleKeys),
    });
  }, [isOpen, isEdit, selectedRole, selectedRoleKeys, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if(values?.permissionKeys?.length === 0){
        message.error(t("roles.validation.permission_keys_required"));
        return;
      }
      setSaving(true);
      await onSave(values);
      onClose();
      form.resetFields();
    } catch (err: any) {
      console.log(err);
    } finally {
      setSaving(false);
    }
  };

  return (
      <CustomModal
        title={isEdit ? t("roles.edit_permissions") : t("roles.create")}
        isOpen={isOpen}
        onClose={() => {
          form.resetFields();
          onClose();
        }}
        onSave={handleOk}
        saveText={t("save")}
        loading={saving}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ name: "", description: "", permissionKeys: [] }}
        >
          <Form.Item
            label={t("roles.form.role_name")}
            name="name"
            rules={!isEdit ? [{ required: true, message: t("roles.validation.role_name_required") || "" }] : []}
          >
            <Input placeholder={t("roles.placeholder.role_name") || ""} disabled={isEdit} />
          </Form.Item>

          <Form.Item label={t("roles.form.description")} name="description">
            <Input.TextArea rows={3} placeholder={t("roles.placeholder.optional") as string} disabled={isEdit} />
          </Form.Item>

          <Divider style={{ margin: "8px 0 12px" }} />
          <div className="d-flex flex-column gap-2">
            <Typography.Text strong>{t("roles.permissions")}</Typography.Text>

            <Alert message={t("roles.form.alert_create_permission")} type="info" showIcon className="d-flex justify-content-between" />

            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t("roles.form.select_permissions_hint")}
            </Typography.Text>
          </div>

          <Form.Item name="permissionKeys">
            <PermissionSelector
              permissions={perms} // full catalog for adding/removing
              value={form.getFieldValue("permissionKeys")} // preselected from selectedRole
              onChange={(keys) => form.setFieldsValue({ permissionKeys: keys })}
              loading={loading}
            />
          </Form.Item>
        </Form>
      </CustomModal>
    );
  };

  export default AddRoleModal;
